import React, { ChangeEvent } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { TextInput } from '../TextField/TextInput';
import { SlotProps, TextFieldOwnerState, TextFieldProps } from '@mui/material';
import { getErrorKey, isEmptyValue } from '~/helpers/utils';

type TFormInputProps = Omit<TextFieldProps, 'ref'> & {
  name: string;
  dataTestId?: string;
  errorTouchedOnly?: boolean;
  rules?: Omit<RegisterOptions, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  disableFormOnChange?: boolean;
  normalizeOnBlur?: (value: string) => string;
  inputProps?: SlotProps<'input', {}, TextFieldOwnerState> & { 'data-testid'?: string };
};

export const FormInput = ({
  errorTouchedOnly,
  rules,
  dataTestId,
  disableFormOnChange,
  normalizeOnBlur,
  inputProps,
  ...props
}: TFormInputProps) => {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      control={control}
      name={props.name}
      rules={rules}
      render={({ field: { ref, onChange, ...field }, fieldState, formState }) => {
        const showFieldError =
          (errorTouchedOnly
            ? fieldState.isTouched
            : fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

        const fieldErrorText = showFieldError ? (fieldState.error?.message as string) : '';
        const isError = !!props.error || showFieldError;
        const errorHelperText = (props.helperText as string) ?? fieldErrorText;

        const testId = dataTestId ?? field.name;
        const errorDataTestId = `${testId}-${getErrorKey(errorHelperText)}`;

        const handleChange = (newValue: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          !disableFormOnChange && onChange(newValue);
          props.onChange?.(newValue);
        };

        return (
          <TextInput
            {...field}
            value={isEmptyValue(field?.value) ? '' : field?.value}
            inputRef={ref}
            {...props}
            onChange={handleChange}
            error={isError}
            helperText={errorHelperText}
            slotProps={{
              ...props.slotProps,
              htmlInput: { ...props?.slotProps?.htmlInput, 'data-testid': testId, ...inputProps },
              formHelperText: {
                'data-testid': errorDataTestId,
              } as React.HTMLAttributes<HTMLParagraphElement>,
            }}
            onBlur={(e) => {
              field.onBlur();
              props?.onBlur?.(e);
              setValue(field.name, normalizeOnBlur?.(e.target.value) ?? e.target.value.trim(), {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        );
      }}
    />
  );
};
